import React from "react";
import { ActivityData, NewActivity } from "@/entities/activities/types";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { EntityData } from "@/services/api/types";
import CallActivityModal from "@/components/layouts/Activities/ActivityModal/CallActivityModal";
import MeetingActivityModal from "@/components/layouts/Activities/ActivityModal/MeetingActivityModal";
import EmailActivityModal from "@/components/layouts/Activities/ActivityModal/EmailActivityModal";
import TextActivityModal from "@/components/layouts/Activities/ActivityModal/TextActivityModal";
import NDAActivityModal from "@/components/layouts/Activities/ActivityModal/NDAActivityModal";
import QuoteActivityModal from "@/components/layouts/Activities/ActivityModal/QuoteActivityModal";
import NoteActivityModal from "@/components/layouts/Activities/ActivityModal/NoteActivityModal";

export interface ActivityModalState {
  newActivityType: NewActivity | null;
  isOpen: boolean;
  isEditMode: boolean;
  editActivityData?: ActivityData | null;
}

interface ActivityModalProps {
  isOpen: boolean;
  entityData: EntityData | undefined;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  resetPage: () => void;
  entityType: EntityType;
  pageName: string;
  isEditMode: boolean;
  editActivityData?: ActivityData | null;
  onImportTranscript?: (activity: ActivityData) => void;
  onExportTranscript?: (activity: ActivityData) => void;
}

interface UseActivityModalsParams {
  activityModalState: ActivityModalState;
  isDropdownClosing: boolean;
  entityData: EntityData | undefined;
  setIsActivityModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  resetActivitiesInfinite: () => void;
  entityType: EntityType;
  pageName: string;
  onImportTranscript?: (activity: ActivityData) => void;
  onExportTranscript?: (activity: ActivityData) => void;
}

const ACTIVITY_MODAL_CONFIG = {
  [NewActivity.CALL]: CallActivityModal,
  [NewActivity.MEETING]: MeetingActivityModal,
  [NewActivity.EMAIL]: EmailActivityModal,
  [NewActivity.TEXT]: TextActivityModal,
  [NewActivity.NDA]: NDAActivityModal,
  [NewActivity.QUOTE]: QuoteActivityModal,
  [NewActivity.NOTE]: NoteActivityModal
} as const;

export const useActivityModals = ({
  activityModalState,
  isDropdownClosing,
  entityData,
  setIsActivityModalOpen,
  resetActivitiesInfinite,
  entityType,
  pageName,
  onImportTranscript,
  onExportTranscript
}: UseActivityModalsParams) => {
  const commonProps: Omit<ActivityModalProps, "isOpen"> = {
    entityData,
    setIsOpen: setIsActivityModalOpen,
    resetPage: resetActivitiesInfinite,
    entityType,
    pageName,
    isEditMode: activityModalState.isEditMode,
    editActivityData: activityModalState.editActivityData,
    onImportTranscript,
    onExportTranscript
  };

  const renderModals = () => {
    return Object.entries(ACTIVITY_MODAL_CONFIG).map(
      ([activityType, ModalComponent]) => {
        const newActivityType = Number(activityType) as NewActivity;
        const isOpen =
          activityModalState.isOpen &&
          activityModalState.newActivityType === newActivityType &&
          !isDropdownClosing;

        // Keyed on the edited activity's id, not just its type, but only for
        // the entry currently designated as active: FormNew's useForm only
        // reads defaultValues on mount, so reusing the same modal instance
        // across different activities of the same type left fields (e.g.
        // the tag checklist) hydrated from whichever activity's data was
        // present at the last mount. Every entry reads the same shared
        // editActivityData, so scoping the id suffix to the active type
        // keeps the other six (irrelevant) modal types from being
        // force-remounted on every edit, and gating on newActivityType
        // rather than isOpen avoids remounting the active modal on a plain
        // close/reopen of the same activity.
        const modalKey =
          activityModalState.newActivityType === newActivityType
            ? `${newActivityType}-${activityModalState.editActivityData?.id ?? "new"}`
            : newActivityType;

        return (
          <ModalComponent key={modalKey} isOpen={isOpen} {...commonProps} />
        );
      }
    );
  };

  return {
    modals: renderModals()
  };
};
